import React, { useState, useEffect, useRef } from 'react';
import { consultationService } from '../services/api';
import './CIE10Selector.css';

const CIE10Selector = ({ selectedDiagnostics, onChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const transientErrorWarnedRef = useRef(false);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      const trimmedQuery = searchQuery.trim();
      const looksLikeCode = /^[a-z]\d?/i.test(trimmedQuery);
      if (trimmedQuery.length >= 2 || looksLikeCode) {
        searchCIE10();
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(delaySearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const searchCIE10 = async () => {
    setLoading(true);
    try {
      const result = await consultationService.searchCIE10(searchQuery);
      if (result.success) {
        setSearchResults(result.data);
        setShowResults(true);
        transientErrorWarnedRef.current = false;
      }
    } catch (error) {
      if (error?.response?.status === 530) {
        if (!transientErrorWarnedRef.current) {
          console.warn('Búsqueda CIE-10 temporalmente no disponible (túnel/red).');
          transientErrorWarnedRef.current = true;
        }
        setSearchResults([]);
        setShowResults(false);
      } else {
        console.error('Error al buscar CIE-10:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const addDiagnostic = (diagnostic) => {
    // Evitar duplicados
    const exists = selectedDiagnostics.some(d => d.codigo === diagnostic.codigo);
    if (!exists) {
      onChange([...selectedDiagnostics, diagnostic]);
    }
    setSearchQuery('');
    setShowResults(false);
  };

  const removeDiagnostic = (codigo) => {
    onChange(selectedDiagnostics.filter(d => d.codigo !== codigo));
  };

  return (
    <div className="cie10-selector">
      <div className="search-section">
        <label>Buscar Diagnóstico CIE-10</label>
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="Buscar por código o descripción (ej: J00, diabetes, hipertensión)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchResults.length > 0 && setShowResults(true)}
          />
          {loading && <div className="search-spinner"></div>}
        </div>

        {showResults && searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((diagnostic) => (
              <div
                key={diagnostic.codigo}
                className="search-result-item"
                onClick={() => addDiagnostic(diagnostic)}
              >
                <span className="diagnostic-code">{diagnostic.codigo}</span>
                <span className="diagnostic-desc">{diagnostic.descripcion}</span>
                {diagnostic.categoria && (
                  <span className="diagnostic-category">{diagnostic.categoria}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {showResults && searchResults.length === 0 && !loading && searchQuery.trim().length >= 1 && (
          <div className="no-results">
            No se encontraron diagnósticos para "{searchQuery}"
          </div>
        )}
      </div>

      <div className="selected-section">
        <label>
          Diagnósticos Seleccionados 
          <span className="count-badge">{selectedDiagnostics.length}</span>
        </label>
        
        {selectedDiagnostics.length === 0 ? (
          <div className="empty-state">
            <p>No hay diagnósticos seleccionados</p>
            <p className="empty-hint">Busque y seleccione diagnósticos del catálogo CIE-10</p>
          </div>
        ) : (
          <div className="selected-list">
            {selectedDiagnostics.map((diagnostic) => (
              <div key={diagnostic.codigo} className="selected-item">
                <div className="selected-info">
                  <span className="selected-code">{diagnostic.codigo}</span>
                  <span className="selected-desc">{diagnostic.descripcion}</span>
                </div>
                <button
                  type="button"
                  onClick={() => removeDiagnostic(diagnostic.codigo)}
                  className="remove-btn"
                  title="Eliminar diagnóstico"
                >
                  x
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CIE10Selector;
